import { useMemo } from 'react'
import * as Progress from '@radix-ui/react-progress'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import { Pause, Play, Trash2, RotateCcw, X, Check, AlertCircle, Loader2 } from 'lucide-react'
import { useUpload } from './UploadContext'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { UploadStatus } from '@/types/upload'

export function UploadQueue() {
  const {
    queue,
    isPaused,
    currentUploadId,
    pauseAll,
    resumeAll,
    clearAll,
    clearCompleted,
    retryFailed,
    removeFromQueue,
    retryItem,
  } = useUpload()

  const stats = useMemo(() => {
    const completed = queue.filter(i => i.status === 'complete').length
    const failed = queue.filter(i => i.status === 'failed').length
    const pending = queue.filter(i => i.status === 'queued' || i.status === 'processing').length
    const uploading = queue.filter(i => i.status === 'uploading').length
    return { completed, failed, pending, uploading, total: queue.length }
  }, [queue])

  if (queue.length === 0) {
    return (
      <div className="border-t border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-400">Upload Queue</h3>
        </div>
        <div className="mt-2 text-sm text-zinc-600">
          No uploads in queue. Select a date and add photos to start.
        </div>
      </div>
    )
  }

  return (
    <div className="border-t border-zinc-800 p-4">
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <h3 className="text-sm font-medium text-white">Upload Queue</h3>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            {stats.uploading > 0 && (
              <span className="text-blue-400">{stats.uploading} uploading</span>
            )}
            {stats.pending > 0 && (
              <span>{stats.pending} pending</span>
            )}
            {stats.completed > 0 && (
              <span className="text-green-400">{stats.completed} completed</span>
            )}
            {stats.failed > 0 && (
              <span className="text-red-400">{stats.failed} failed</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Pause/Resume */}
          <Button
            variant="ghost"
            size="sm"
            onClick={isPaused ? resumeAll : pauseAll}
            className="text-xs"
          >
            {isPaused ? (
              <>
                <Play className="w-3 h-3 mr-1" />
                Resume
              </>
            ) : (
              <>
                <Pause className="w-3 h-3 mr-1" />
                Pause
              </>
            )}
          </Button>

          {/* Retry failed */}
          {stats.failed > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={retryFailed}
              className="text-xs text-yellow-500"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Retry Failed
            </Button>
          )}

          {/* Clear completed */}
          {stats.completed > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCompleted}
              className="text-xs"
            >
              Clear Done
            </Button>
          )}

          {/* Clear all */}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="text-xs text-red-500"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Clear All
          </Button>
        </div>
      </div>

      {/* Queue items with horizontal scroll */}
      <ScrollArea.Root className="w-full">
        <ScrollArea.Viewport className="w-full">
          <div className="flex gap-3 pb-2">
            {queue.map(item => (
              <UploadQueueItem
                key={item.id}
                item={item}
                isCurrent={item.id === currentUploadId}
                onRemove={() => removeFromQueue(item.id)}
                onRetry={() => retryItem(item.id)}
              />
            ))}
          </div>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="horizontal"
          className="flex h-2 touch-none select-none flex-col bg-zinc-900 p-0.5"
        >
          <ScrollArea.Thumb className="relative flex-1 rounded-full bg-zinc-700" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    </div>
  )
}

interface UploadQueueItemProps {
  item: {
    id: string
    filename: string
    status: UploadStatus
    progress: number
    previewUrl?: string
    error?: string
  }
  isCurrent: boolean
  onRemove: () => void
  onRetry: () => void
}

function UploadQueueItem({ item, isCurrent, onRemove, onRetry }: UploadQueueItemProps) {
  return (
    <div
      className={cn(
        'flex-shrink-0 w-28 bg-zinc-900 rounded-lg overflow-hidden',
        'border-2 transition-all',
        isCurrent ? 'border-blue-500' : 'border-transparent',
        item.status === 'failed' && 'border-red-500'
      )}
    >
      {/* Preview image */}
      <div className="relative aspect-[4/3] bg-zinc-800">
        {item.previewUrl ? (
          <img
            src={item.previewUrl}
            alt={item.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-zinc-600 animate-spin" />
          </div>
        )}

        {/* Status overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <StatusIcon status={item.status} />
        </div>

        {/* Remove button */}
        <button
          onClick={onRemove}
          className="absolute top-1 right-1 p-1 bg-black/50 rounded-full hover:bg-black/80 transition-colors"
        >
          <X className="w-3 h-3 text-white" />
        </button>
      </div>

      {/* Progress bar */}
      {(item.status === 'uploading' || item.status === 'processing') && (
        <Progress.Root
          value={item.progress}
          className="h-1 bg-zinc-800 overflow-hidden"
        >
          <Progress.Indicator
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${item.progress}%` }}
          />
        </Progress.Root>
      )}

      {/* Info */}
      <div className="p-2">
        <p
          className="text-xs text-zinc-400 truncate"
          title={item.filename}
        >
          {item.filename}
        </p>
        <div className="flex items-center justify-between mt-1">
          <StatusText status={item.status} progress={item.progress} />
          {item.status === 'failed' && (
            <button
              onClick={onRetry}
              className="text-xs text-yellow-500 hover:text-yellow-400"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function StatusIcon({ status }: { status: UploadStatus }) {
  switch (status) {
    case 'complete':
      return (
        <div className="w-8 h-8 rounded-full bg-green-500/80 flex items-center justify-center">
          <Check className="w-5 h-5 text-white" />
        </div>
      )
    case 'failed':
      return (
        <div className="w-8 h-8 rounded-full bg-red-500/80 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-white" />
        </div>
      )
    case 'uploading':
    case 'processing':
      return null // Progress bar shows status
    default:
      return null
  }
}

function StatusText({ status, progress }: { status: UploadStatus; progress: number }) {
  switch (status) {
    case 'processing':
      return <span className="text-xs text-yellow-500">Processing...</span>
    case 'queued':
      return <span className="text-xs text-zinc-500">Queued</span>
    case 'uploading':
      return <span className="text-xs text-blue-400">{progress}%</span>
    case 'complete':
      return <span className="text-xs text-green-500">Done</span>
    case 'failed':
      return <span className="text-xs text-red-500">Failed</span>
    case 'paused':
      return <span className="text-xs text-zinc-500">Paused</span>
    default:
      return null
  }
}
